import { COUNTER_PARTY_STATUS } from '#client/constants/payees';

/**
 * The set of variants a counterparty status can be classified into.
 * `notRelinkable` is a terminal state (e.g. Unit's stored customer info
 * permanently no longer matches the linked bank connection) where the
 * customer must create a brand new connection instead of relinking.
 */
export const COUNTERPARTY_STATUS_VARIANT = {
  ACTIVE: 'active',
  ARCHIVED: 'archived',
  NOT_RELINKABLE: 'notrelinkable'
};

/**
 * Classify a raw counterparty `status` value into one of the known
 * variants. Any status that isn't `active` and isn't the terminal
 * `notRelinkable` state is treated as `archived` (this includes the
 * legacy `archived` status as well as any other non-active status).
 *
 * @param {string} status - the raw counterparty status value
 * @returns {string} one of COUNTERPARTY_STATUS_VARIANT
 */
export const getCounterpartyStatusVariant = (status) => {
  if (status === COUNTER_PARTY_STATUS.NOT_RELINKABLE) {
    return COUNTERPARTY_STATUS_VARIANT.NOT_RELINKABLE;
  }

  if (status === COUNTER_PARTY_STATUS.ACTIVE) {
    return COUNTERPARTY_STATUS_VARIANT.ACTIVE;
  }

  return COUNTERPARTY_STATUS_VARIANT.ARCHIVED;
};

/**
 * Check whether a raw counterparty status is the active variant.
 *
 * @param {string} status - the raw counterparty status value
 * @returns {boolean} true when the counterparty is active
 */
export const isActiveStatus = (status) =>
  getCounterpartyStatusVariant(status) === COUNTERPARTY_STATUS_VARIANT.ACTIVE;

/**
 * Check whether a raw counterparty status is the terminal notRelinkable variant.
 *
 * @param {string} status - the raw counterparty status value
 * @returns {boolean} true when the counterparty can never be relinked
 * and instead requires a brand new connection
 */
export const isNotRelinkableStatus = (status) =>
  getCounterpartyStatusVariant(status) === COUNTERPARTY_STATUS_VARIANT.NOT_RELINKABLE;

export const RELINK_ACTION_LABEL = {
  RELINK: 'Relink',
  CREATE_NEW_CONNECTION: 'Create new connection'
};

/**
 * Get the correct call-to-action label for a non-active counterparty.
 *
 * @param {string} status - the raw counterparty status value
 * @returns {string} 'Create new connection' for the terminal notRelinkable
 * state, otherwise the standard 'Relink' label.
 */
export const getRelinkActionLabel = (status) =>
  isNotRelinkableStatus(status)
    ? RELINK_ACTION_LABEL.CREATE_NEW_CONNECTION
    : RELINK_ACTION_LABEL.RELINK;
